import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Paper,
  Box,
  Chip,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import { ArrowBack, GitHub, Code } from "@mui/icons-material";
import ProjectLatestCommit from "../../components/ProjectLatestCommit";
// import { getProjectById } from "../../services/api";
import AppBreadcrumbs from '../../components/AppBreadcrumbs';
import useProjectStore from "../../store/useProjectStore";
import { useDashboardShell } from '../../context/DashboardShellContext';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const shell = useDashboardShell();
  const workspace = shell?.workspacePath ?? '/student/dashboard';
  const [error, setError] = useState(null);
  const {
    projectById,
    loadingProjectById,
    errorProjectById,
    fetchProjectById,
  } = useProjectStore();
  const project = projectById[id];
  const loading = loadingProjectById[id];

  useEffect(() => {
    const load = async () => {
      try {
        await fetchProjectById(id);
      } catch (err) {
        console.error("Error fetching project details:", err);
        setError(err.response?.data?.message || "Failed to load project details");
      }
    };
    if (id && !project) load();
  }, [id, fetchProjectById, project]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || errorProjectById[id]) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || errorProjectById[id]?.message || "Error loading project"}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  if (!project) return null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 1 }}
      >
        Back to Dashboard
      </Button> */}

      <AppBreadcrumbs
        items={[
          { label: 'Dashboard', path: workspace },
          { label: project.title || 'Project' },
        ]}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 4, mb: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={2}
            >
              <Typography variant="h4" component="h1" gutterBottom>
                {project.title}
              </Typography>
              {project.status && (
                <Chip
                  label={project.status}
                  color={project.status === "evaluated" ? "success" : "primary"}
                  variant="outlined"
                />
              )}
            </Box>
            {project.student ? (
              <Box>
                <Typography variant="subtitle1">
                  {project.student.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {project.student.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {project.student.branch} - Year {project.student.yearOfStudy}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {project.student.email}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Student information not available
              </Typography>
            )}
            <Typography
              variant="h6"
              color="text.secondary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              Description
            </Typography>
            <Typography variant="body1" paragraph>
              {project.description || "No description provided."}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Tech Stack
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {project.techStack && (
                  <Chip
                    label={project.techStack}
                    icon={<Code />}
                    size="small"
                  />
                )}
              </Box>
            </Box>

            {project.repoUrl && (
              <Box mt={4}>
                <Button
                  variant="outlined"
                  startIcon={<GitHub />}
                  href={project.repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Repository
                </Button>
              </Box>
            )}
          </Paper>
          <ProjectLatestCommit projectId={id} />

          {/* {project.evaluation && (
            <Paper sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom>Faculty Evaluation</Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="h3" color="primary">
                  {project.evaluation.score}/100
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Total Score
                </Typography>
              </Box>
              <Typography variant="body1">
                <strong>Feedback:</strong> {project.evaluation.feedback}
              </Typography>
            </Paper>
          )} */}
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProjectDetails;
